import { type JSX, type MouseEvent, useCallback } from 'react';

import { EloPaginationNumber, EloSkeleton, Text } from '@plurall/elo-v3';
import { useNavigate } from '@tanstack/react-router';

import { LearningPathCard } from '@/features/learning-path';

import {
  LearningPathsListPageGridFactory,
  LearningPathsListPagePaginationFactory,
  LearningPathsListPageStateFactory,
} from './learning-paths-list-page.factory';
import { LearningPathsToolbar } from '../../components/LearningPathsToolbar';
import { ESSAYS_PAGE_SIZE, useEssaysContext } from '../../context/EssaysContext';

const SKELETON_CARDS = Array.from({ length: 6 }, (_, index) => index);

function isPlainLeftClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

export function LearningPathsListPage(): JSX.Element {
  const { isStudent, schoolId, page, setPage, learningPaths, isLoading, isError } = useEssaysContext();
  const navigate = useNavigate();

  const getManagerHref = useCallback(
    (id: number) => `/trilha-de-aprendizagem/${id}${schoolId !== null ? `?escola=${schoolId}` : ''}`,
    [schoolId]
  );

  const handleManagerTitleClick = useCallback(
    (id: number) => (event: MouseEvent<HTMLAnchorElement>) => {
      if (!isPlainLeftClick(event)) return;
      event.preventDefault();
      void navigate({
        to: '/trilha-de-aprendizagem/$id',
        params: { id: String(id) },
        search: { escola: schoolId ?? undefined },
      });
    },
    [navigate, schoolId]
  );

  const renderContent = (): JSX.Element => {
    if (isLoading) {
      return (
        <LearningPathsListPageGridFactory>
          {SKELETON_CARDS.map((index) => (
            <EloSkeleton.Block
              key={index}
              shape='rectangle'
              style={{ height: '20rem' }}
            />
          ))}
        </LearningPathsListPageGridFactory>
      );
    }

    if (isError) {
      return (
        <LearningPathsListPageStateFactory>
          <Text fontWeight='bold'>Não foi possível carregar as trilhas</Text>
          <Text>Tente novamente em alguns instantes.</Text>
        </LearningPathsListPageStateFactory>
      );
    }

    const items = learningPaths?.data ?? [];

    if (items.length === 0) {
      return (
        <LearningPathsListPageStateFactory>
          <Text fontWeight='bold'>Nenhuma trilha encontrada</Text>
          <Text>Tente ajustar a busca ou os filtros.</Text>
        </LearningPathsListPageStateFactory>
      );
    }

    return (
      <>
        <LearningPathsListPageGridFactory>
          {items.map((item) =>
            isStudent ? (
              // TODO: link to the student's learning path page once it exists.
              <LearningPathCard
                key={item.id}
                data={item}
              />
            ) : (
              <LearningPathCard
                key={item.id}
                data={item}
                href={getManagerHref(item.id)}
                onTitleClick={handleManagerTitleClick(item.id)}
              />
            )
          )}
        </LearningPathsListPageGridFactory>

        {learningPaths && learningPaths.pageable.totalElements > ESSAYS_PAGE_SIZE && (
          <LearningPathsListPagePaginationFactory>
            {/* EloPaginationNumber ignores the controlled `page` prop (elo-v3 0.20.1), so the current page is set
                through `defaultPage` and kept in sync by remounting on `key`. */}
            <EloPaginationNumber
              key={page}
              count={learningPaths.pageable.totalElements}
              pageSize={ESSAYS_PAGE_SIZE}
              defaultPage={page}
              onPageChange={({ page: nextPage }) => setPage(nextPage)}
            />
          </LearningPathsListPagePaginationFactory>
        )}
      </>
    );
  };

  return (
    <section>
      <LearningPathsToolbar />
      {renderContent()}
    </section>
  );
}
